import { useState, type CSSProperties, type ReactNode } from "react";
import { Button } from "@legends/ui";

// Shared Legends Cave building blocks for the admin-portal consoles (Admin,
// Security, Impersonate): a titled surface, inline alert text, and list rows.

export const errMsg = (err: unknown) => (err instanceof Error ? err.message : String(err));

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Concise local date/time, e.g. "Mon Jul 20 · 7:00 PM". */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  let h = d.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h %= 12;
  if (h === 0) h = 12;
  const m = d.getMinutes();
  const mm = m < 10 ? `0${m}` : `${m}`;
  return `${DOW[d.getDay()]} ${MON[d.getMonth()]} ${d.getDate()} · ${h}:${mm} ${ampm}`;
}

/** Cents → "$X.XX". */
export function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Minimal shape needed to reason about a pro-rated multi-event ticket (ADR 0161). */
export interface Proratable {
  priceCents: number;
  proratedPriceCents: number;
  eventCount: number;
  totalEventCount: number;
  /** Admission-open covered events (upcoming, in-window, or live — ADR 0252). */
  eventIds?: string[];
}

/** True when the ticket's current price is discounted for CaveCasts already gone. */
export function isProrated(t: Proratable): boolean {
  return t.proratedPriceCents < t.priceCents && t.totalEventCount > 1;
}

/**
 * "N of M CaveCasts left" note for a pro-rated multi-event pass, or "" when the
 * price isn't discounted (nothing has passed, or it's a single event).
 */
export function proratedNote(t: Proratable): string {
  // Count the admission-open set (eventIds) when present, so the final game of a series
  // reads "1 of 7 left" while it is LIVE (and still sellable, ADR 0252) — eventCount is
  // forward-looking and would say "0 of 7".
  const left = Math.max(t.eventCount, t.eventIds?.length ?? 0);
  return isProrated(t) ? `${left} of ${t.totalEventCount} CaveCasts left` : "";
}

/** A ticket kind as a terse uppercase label, e.g. "all_access" → "ALL ACCESS". */
export function formatKind(kind: string): string {
  return kind.replace(/_/g, " ").toUpperCase();
}

export const alertStyle: CSSProperties = {
  margin: "10px 0 0",
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-2xs)",
  letterSpacing: "var(--ls-label)",
  color: "var(--alert-2)",
};

export const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "7px 0",
  borderTop: "1px solid var(--border-faint)",
};

/** A responsive card grid for operator list surfaces (Events / Recordings /
 *  Pre-post-roll): as many cards per row as the width allows — it keeps filling with
 *  MORE columns as the screen gets wider (2 on a laptop, 3–4 on a desktop, 5+ on a
 *  wide monitor), never capping the column count. Each card is bounded 320–480px
 *  (`minmax`), so a short list on a very wide screen stays card-sized rather than
 *  stretching a couple of cards across the whole row. */
export const cardGridStyle: CSSProperties = {
  display: "grid",
  // min(320px, 100%) so a container narrower than a card doesn't overflow.
  gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 480px))",
  gap: 16,
  alignItems: "start",
};

/** A bordered, raised content card — the standalone-item surface used inside
 *  `cardGridStyle` (an event, a recording, a roll item). It sits a full tier ABOVE
 *  its container (`cardGroupStyle` / a `Section`) so the cards clearly offset and the
 *  inter-card gaps read as spacing (the container is one grey darker). */
export const cardStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: 14,
  border: "1px solid var(--line-strong, rgba(255,255,255,0.14))",
  borderRadius: "var(--radius-lg)",
  background: "var(--ink-700, #24242b)",
  boxShadow: "var(--shadow-1, 0 1px 2px rgba(0,0,0,0.45))",
  minWidth: 0,
};

/** The mid-grey container panel a card grid sits inside (Events/Roll get this from
 *  their `Section`; Recordings wraps its grid in this so it matches). A tier below
 *  the cards, a tier above the page — so the cards are offset and the panel reads as
 *  "the lighter grey background". */
export const cardGroupStyle: CSSProperties = {
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-lg)",
  padding: 16,
};

/** The lifecycle of a launch-critical async read (ADR 0049): loading while it's
 *  in flight, ready once it resolves, error when the fetch itself failed. Kept
 *  distinct from "resolved-but-empty" so a Fan can tell "nothing scheduled" from
 *  "the service is down". */
export type LoadStatus = "loading" | "ready" | "error";

/**
 * A distinct loading / empty / error note for a launch-critical read (ADR 0049).
 * A failed fetch renders an accessible `role="alert"` message with a Retry
 * action instead of collapsing into a blank that reads as "nothing here"; the
 * empty state is separate copy that only shows once the read has actually
 * resolved with no rows.
 */
export function AsyncState({
  status,
  loadingText = "Loading…",
  emptyText = "Nothing here yet.",
  errorText = "Couldn't load — please try again.",
  onRetry,
  compact = false,
}: {
  /** "empty" is the resolved-with-no-rows case (distinct from "loading"). */
  status: "loading" | "empty" | "error";
  loadingText?: ReactNode;
  emptyText?: ReactNode;
  errorText?: ReactNode;
  onRetry?: () => void;
  compact?: boolean;
}) {
  const noteStyle: CSSProperties = {
    margin: 0,
    fontSize: compact ? "var(--fs-xs)" : "var(--fs-body-2)",
    color: "var(--text-dim)",
  };
  if (status === "loading") {
    return (
      <p role="status" aria-live="polite" style={noteStyle}>
        {loadingText}
      </p>
    );
  }
  if (status === "error") {
    return (
      <div role="alert" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
        <span style={{ ...noteStyle, color: "var(--alert-2)" }}>{errorText}</span>
        {onRetry && (
          <Button size="sm" variant="muted" onClick={onRetry}>
            Retry
          </Button>
        )}
      </div>
    );
  }
  return <p style={noteStyle}>{emptyText}</p>;
}

export const monoLabelStyle: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-2xs)",
  letterSpacing: "var(--ls-label)",
  textTransform: "uppercase",
  color: "var(--text-label)",
};

/** Recording opt-out control (ADR 0080): a labelled Auto / On / Off segmented
 *  choice composed from @legends/ui Buttons. `value` is boolean | null (null =
 *  inherit the next level up). Set `allowInherit={false}` for the platform-level
 *  toggle (On / Off only). `inheritHint` describes what Auto inherits. */
export function RecordChoice({
  value,
  onChange,
  allowInherit = true,
  inheritHint,
  label = "Record",
  busy = false,
}: {
  value: boolean | null;
  onChange: (v: boolean | null) => void;
  allowInherit?: boolean;
  inheritHint?: string;
  label?: string;
  busy?: boolean;
}) {
  const opt = (v: boolean | null, text: string) => (
    <Button
      size="sm"
      variant={value === v ? "accent" : "muted"}
      aria-pressed={value === v}
      aria-label={`${label}: ${text}`}
      disabled={busy}
      onClick={() => onChange(v)}
    >
      {text}
    </Button>
  );
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span style={monoLabelStyle}>{label}</span>
      {allowInherit && opt(null, "Auto")}
      {opt(true, "On")}
      {opt(false, "Off")}
      {allowInherit && value == null && inheritHint && (
        <span style={{ fontSize: "var(--fs-2xs)", color: "var(--text-dim)" }}>{inheritHint}</span>
      )}
    </span>
  );
}

/** Condensed Oswald display face (ADR 0018) — headlines, legend names, big
 *  stat/price figures. Numbers use tabular numerals so columns line up. */
export const displayStyle: CSSProperties = {
  fontFamily: "var(--font-display)",
  letterSpacing: "0.01em",
};

/** Tabular numerals — pair with displayStyle on any numeric figure. */
export const tnum: CSSProperties = {
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: '"tnum" 1',
};

// ── AppShell chrome (ADR 0009) ───────────────────────────────────────────────

/** The LSC crest mark + LEGENDS SPORTS CAVE wordmark, for the shell's
 *  top-left brand slot. Clicking it goes home (ADR 0021) — a plain anchor so it
 *  works in any context and cleanly resets to the host's root. */
export function LegendBrandMark({ size = 24 }: { size?: number }) {
  return (
    <img
      src="/legend-logo.png"
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      style={{ display: "block", width: size, height: size, objectFit: "contain", flex: "0 0 auto" }}
    />
  );
}

export function LegendsBrand() {
  return (
    <a
      href="/"
      aria-label="Legend Sports Cave — home"
      style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none", cursor: "pointer" }}
    >
      <LegendBrandMark />
      <span
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--fs-title)",
          fontWeight: 900,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--gold)",
        }}
      >
        Legend Sports Cave
      </span>
    </a>
  );
}

/** A mono-uppercase top-bar title for the shell. */
export function ShellTitle({ children }: { children: ReactNode }) {
  return <span style={{ ...monoLabelStyle, color: "var(--text-hi)" }}>{children}</span>;
}

/** Sidebar footer: the operator's name (accent mono) + a Sign out button. */
export function SidebarUser({ name, onSignOut }: { name: string; onSignOut: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "var(--fs-2xs)",
          letterSpacing: "var(--ls-label)",
          color: "var(--accent)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </span>
      <Button size="sm" variant="muted" onClick={onSignOut}>
        Sign out
      </Button>
    </div>
  );
}

// Section — a titled surface. With `collapsible` (LSC-328) the whole header becomes
// a toggle so an operator can fold away an area on a long console; `defaultOpen`
// (default true) sets the initial state, and `count` shows a small tally in the
// header. Non-collapsible callers are unchanged (the title is a plain <h2>).
export function Section({
  title,
  children,
  collapsible = false,
  defaultOpen = true,
  count,
}: {
  title: string;
  children: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  count?: number;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const headStyle: CSSProperties = {
    margin: 0,
    fontFamily: "var(--font-mono)",
    fontSize: "var(--fs-2xs)",
    fontWeight: 600,
    letterSpacing: "var(--ls-caps)",
    textTransform: "uppercase",
    color: "var(--text-label)",
  };
  return (
    <section
      style={{
        background: "var(--surface-2)",
        border: "1px solid var(--border)",
        padding: collapsible ? "0" : "18px 20px 16px",
      }}
    >
      {collapsible ? (
        <>
          <h2 style={{ margin: 0 }}>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              style={{
                ...headStyle,
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "16px 20px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <span aria-hidden style={{ display: "inline-block", transition: "transform 120ms", transform: open ? "rotate(90deg)" : "none", color: "var(--accent, #c9a227)" }}>▸</span>
              {title}
              {count != null && <span style={{ color: "var(--text-dim)", fontWeight: 400 }}>· {count}</span>}
            </button>
          </h2>
          {open && <div style={{ padding: "0 20px 16px" }}>{children}</div>}
        </>
      ) : (
        <>
          <h2 style={headStyle}>{title}</h2>
          {children}
        </>
      )}
    </section>
  );
}
